import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
    BasicMachineFunctionsService,
    t_FunctionConfig
} from 'src/app/services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import {
    DidDataRetrievalService,
    t_DataIdentifier,
    t_DataPointTimeSeries,
} from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { t_OutputCheckedElement } from '../basic-view-table/basic-view-table.component';
import { dataIdentifierEqual } from 'src/app/services/helper/did-handling-helper';

@Component({
    selector: 'app-basic-view-container',
    templateUrl: './basic-view-container.component.html',
    styleUrls: ['./basic-view-container.component.scss'],
})
export class BasicViewContainerComponent implements OnInit, OnDestroy {
    public funcName!: string;
    public functionConfig?: t_FunctionConfig;
    public functionIndex?: number;
    public functionConfigLoaded: boolean;
    private dataIdentifierList: t_DataIdentifier[] = [];
    public graphDataIdentifierToDisplay: t_DataIdentifier[] = [];
    public rawDataSet: t_DataPointTimeSeries[] = [];
    public querySubscription: Subscription = new Subscription();
    public started: boolean = false;

    constructor(
        private route: ActivatedRoute,
        public apiConnection: BasicMachineFunctionsService,
        public didConfigConnection: DidConfigService,
        private apiDataConnection: DidDataRetrievalService
    ) {
        this.functionConfigLoaded = false;
    }

    ngOnInit(): void {
        this.route.params.subscribe({
            next: (params) => {
                this.funcName = params['function-name'];
                this.subscribeToFunctionObservable().then(() => {
                    this.apiDataConnection.initService();
                    this.querySubscription = this.apiDataConnection.queryLiveObs.subscribe((queryLiveStatus) => {
                        this.loadDataValues();
                    });
                });
            },
        });
    }

    ngOnDestroy(): void {
        this.querySubscription.unsubscribe();
        this.dataIdentifierList.forEach(data => {
            this.didConfigConnection.removeDidFromQueryList({ecu_id: data.ecu_id, data_id: data.data_id}).subscribe();
        });
        this.apiDataConnection.stopSendQueryAliveState();
        this.apiDataConnection.stopDataQuery();
        this.apiDataConnection.cleanDataPoints();
    }

    // Get Signal when function config to own function is loaded
    private subscribeToFunctionObservable(): Promise<void> {
        return new Promise((resolve) => {
            this.apiConnection.loadedFunctionObservable.subscribe((funcName) => {
                if ((funcName = this.funcName)) {
                    this.loadFunctionConfigFromService();
                    resolve();
                }
            });
        });
    }

    // Called when signal, that function config is loaded is received
    private loadFunctionConfigFromService(): void {
        this.functionIndex = this.apiConnection.basicMachineFunctionsConfig.functions.findIndex((func) => {
            return func.name == this.funcName;
        });

        this.functionConfig = this.apiConnection.basicMachineFunctionsConfig.functions[this.functionIndex];
        if (this.functionConfig.dataNames) {
            this.functionConfigLoaded = true;
            this.setupDataIdentifierList();
        } 
    }

    private setupDataIdentifierList() {
        this.functionConfig?.dataNames.forEach((dataName) => {
            if (dataName.identifier) {
                this.dataIdentifierList.push(dataName.identifier);
            }
        });
    }

    public loadDataValues(): void {
        this.apiDataConnection.getDidsDetails(this.dataIdentifierList).subscribe((response) => {
            if (response.body) {
                this.rawDataSet = this.filterNegativeTimestampsFromRawData(response.body);
            }
        });
    }

    public dataSelected(checkedElement: t_OutputCheckedElement) {
        this.apiConnection.setCheckedStatus(this.funcName, checkedElement.identifier, checkedElement.checked);

        if (checkedElement.checked == true) {
            this.addDataPointToGraph(checkedElement.identifier);
        }
        if (checkedElement.checked == false) {
            this.removeDataPointFromGraph(checkedElement.identifier);
        }
    }

    private addDataPointToGraph(identifierToAdd: t_DataIdentifier) {
        let dataPointAlreadyDisplayed = this.graphDataIdentifierToDisplay.find((dataPointIdentifier) => {
            return dataIdentifierEqual(identifierToAdd, dataPointIdentifier);
        });

        if (dataPointAlreadyDisplayed == undefined) {
            this.graphDataIdentifierToDisplay.push(identifierToAdd);
            this.graphDataIdentifierToDisplay = [...this.graphDataIdentifierToDisplay];
        }
    }

    private removeDataPointFromGraph(identifier: t_DataIdentifier) {
        let indexToRemove: number = this.graphDataIdentifierToDisplay.findIndex((dataPoint) => {
            return dataIdentifierEqual(dataPoint, identifier);
        });

        if (indexToRemove !== -1) {
            this.graphDataIdentifierToDisplay.splice(indexToRemove, 1);
            this.graphDataIdentifierToDisplay = [...this.graphDataIdentifierToDisplay];
        }
    }

    public filterNegativeTimestampsFromRawData(rawDataset: t_DataPointTimeSeries[]) {
        rawDataset.forEach((dataPoint) => {
            dataPoint.data = dataPoint.data?.filter((dataValue) => {
                return new Date(dataValue.timestamp).getTime() != -1;
            });
        });
        return rawDataset;
    }

    public queryStarted(started: boolean) {
        this.started = started;
    }
}
